import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { useEffect, useState } from "react";
import {
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  ButtonGroup,
  ConfirmButton,
  Row,
  CancelButton,
  InputContainer,
  PlaylistListContainer,
  PlaylistItem,
  PlaylistName,
  Loading,
} from "./AddToPlaylistModal.styles";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { ReactComponent as PlusCircleIcon } from "../../assets/icons/plus-circle.svg";
import { useDispatch, useSelector } from "react-redux";

import LottieIcon from "../../components/LottieIcon/LottieIcon";
import checkboxAnimation from "../../assets/animations/radioButton.json";

import {
  selectMyPlaylists,
  selectPlaylistPending,
} from "../../store/slices/playlist/slice";
import {
  addCollectionToPlaylist,
  fetchMyPlaylists,
} from "../../store/slices/playlist/asyncThunk";
import { toast } from "sonner";

const AddToPlaylistModal = ({
  isOpen,
  onClose,
  isCreatingNew,
  setIsCreatingNew,
  collectionId,
  openCreatePlaylistModal,
  isLoading = false,
}) => {
  useLockBodyScroll(isOpen);

  const dispatch = useDispatch();
  const {
    data: playlists,
    isFetched,
    loading,
  } = useSelector(selectMyPlaylists);

  useEffect(() => {
    if (!isFetched && isOpen) {
      dispatch(fetchMyPlaylists());
    }
  }, [dispatch, isFetched, isOpen]);

  const [selectedPlaylists, setSelectedPlaylists] = useState([]);

  const addCollectionPending = useSelector(
    selectPlaylistPending,
  ).addCollectionToPlaylist;

  const handlePlaylistClick = (playlistId) => {
    if (playlistId === "create_new") {
      setIsCreatingNew(true);
      openCreatePlaylistModal();
      onClose();
      setSelectedPlaylists([]);
    } else {
      setIsCreatingNew(false);

      setSelectedPlaylists((prev) =>
        prev.includes(playlistId)
          ? prev.filter((id) => id !== playlistId)
          : [...prev, playlistId],
      );
    }
  };

  const handleConfirm = () => {
    if (selectedPlaylists.length > 0) {
      dispatch(
        addCollectionToPlaylist({
          collectionId,
          playlistIds: selectedPlaylists,
        }),
      )
        .unwrap()
        .then(() => {
          toast.success("Collection added to playlist");
          setSelectedPlaylists([]);
          setIsCreatingNew(false);
          onClose();
        })
        .catch((err) => {
          setSelectedPlaylists([]);
          setIsCreatingNew(false);
          onClose();
        });
    }
  };

  const handleCancel = () => {
    if (!isLoading) {
      setSelectedPlaylists([]);
      setIsCreatingNew(false);
      onClose();
    }
  };

  const isConfirmDisabled = isLoading || selectedPlaylists.length === 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customOverlayStyles={{ zIndex: 3600 }}
    >
      <ModalContainer>
        <ModalContent>
          <Row>
            <ModalTitle>Add to Playlist</ModalTitle>
            <CloseButton onClick={handleCancel}>
              <CloseSvg width={20} height={20} />
            </CloseButton>
          </Row>

          <InputContainer>
            <PlaylistListContainer $paddingRight={playlists.length < 7}>
              {loading && (
                <Loading>
                  <l-line-spinner size="20" stroke="2" speed="1" color="#fff" />
                </Loading>
              )}
              {!loading &&
                playlists &&
                playlists.length > 0 &&
                playlists.map((playlist) => (
                  <PlaylistItem
                    key={playlist._id}
                    onClick={() => {
                      handlePlaylistClick(playlist._id);
                    }}
                    $isSelected={selectedPlaylists.includes(playlist._id)}
                  >
                    <PlaylistName>{playlist.name}</PlaylistName>

                    <LottieIcon
                      animationData={checkboxAnimation}
                      trigger="toggle-reverse"
                      isActive={selectedPlaylists.includes(playlist._id)}
                      onActiveChange={() => handlePlaylistClick(playlist._id)}
                      onClick={() => handlePlaylistClick(playlist._id)}
                      strokeColor={"#4D9DFF"}
                      speed={2}
                      size={24}
                    />
                  </PlaylistItem>
                ))}
            </PlaylistListContainer>

            {!loading && (
              <PlaylistItem
                onClick={() => handlePlaylistClick("create_new")}
                $isSelected={isCreatingNew}
              >
                <PlaylistName>Create new playlist</PlaylistName>
                <PlusCircleIcon />
              </PlaylistItem>
            )}
          </InputContainer>

          <ButtonGroup>
            <CancelButton
              onClick={handleCancel}
              disabled={addCollectionPending}
            >
              Cancel
            </CancelButton>
            <ConfirmButton
              onClick={handleConfirm}
              $type="primary"
              disabled={isConfirmDisabled || addCollectionPending}
            >
              {addCollectionPending ? (
                <l-line-spinner size="20" stroke="2" speed="1" color="#000" />
              ) : (
                "Add"
              )}
            </ConfirmButton>
          </ButtonGroup>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default AddToPlaylistModal;
